import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Hospital, ArrowRight, HeartPulse, Building2, Stethoscope } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { AppointmentBlock, ConcurrencyBlock, MultilingualBlock } from "../components/industries/FlowBlocks";
import { DashboardsBlock, AnalyticsBlock, TrackingBlock } from "../components/industries/DashboardBlocks";
import { CampaignBlock, IntegrationsBlock, ResidencyBlock } from "../components/industries/PlatformBlocks";
import ImpactSection from "../components/shared/ImpactSection";
import ComparisonSection from "../components/shared/ComparisonSection";
import { valueAdds, contactInfo } from "../mock";

const IndustriesPage = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  const blocks = [
    { Comp: AppointmentBlock, data: valueAdds[0] },
    { Comp: ConcurrencyBlock, data: valueAdds[1] },
    { Comp: MultilingualBlock, data: valueAdds[2] },
    { Comp: DashboardsBlock, data: valueAdds[3] },
    { Comp: AnalyticsBlock, data: valueAdds[4] },
    { Comp: TrackingBlock, data: valueAdds[5] },
    { Comp: CampaignBlock, data: valueAdds[6] },
    { Comp: IntegrationsBlock, data: valueAdds[7] },
    { Comp: ResidencyBlock, data: valueAdds[8] },
  ];

  return (
    <div className="min-h-screen bg-[#04060a] text-white">
      <Navbar />
      <main>
        {/* Industry hero */}
        <section className="relative overflow-hidden pb-16 pt-40">
          <div className="bg-grid absolute inset-0" />
          <div className="bg-radial-glow absolute inset-0" />
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <div className="flex flex-wrap items-center gap-3 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                <Link to="/" className="transition-colors hover:text-cyan-300">Industries</Link>
                <span>/</span>
                <span className="text-cyan-300">Healthcare & Hospitals</span>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <h1 className="font-display mt-5 max-w-4xl text-4xl font-bold leading-[1.08] text-white sm:text-5xl lg:text-6xl">
                The AI front desk for <span className="text-cyan-400">modern hospitals</span>
              </h1>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-400">
                From appointment management to data residency — nine capabilities engineered specifically
                for hospitals, clinics and multi-hospital groups. Better access, faster support, happier patients.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-10 flex flex-wrap gap-8">
                {[
                  { icon: HeartPulse, t: "Purpose-built for patient journeys" },
                  { icon: Building2, t: "Clinics to multi-hospital groups" },
                  { icon: Stethoscope, t: "HIS / EMR / PABX native" },
                ].map((f) => (
                  <div key={f.t} className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/10">
                      <f.icon className="h-5 w-5 text-cyan-300" />
                    </div>
                    <span className="max-w-[180px] text-sm font-medium leading-snug text-slate-300">{f.t}</span>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* quick nav chips */}
            <Reveal delay={400}>
              <div className="mt-12 flex flex-wrap gap-2">
                {valueAdds.map((v, i) => (
                  <button
                    key={v.id}
                    onClick={() => document.getElementById(v.id)?.scrollIntoView({ behavior: "smooth" })}
                    className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-slate-300 transition-all duration-200 hover:border-cyan-400/40 hover:bg-cyan-400/10 hover:text-cyan-200"
                  >
                    {String(i + 1).padStart(2, "0")} · {v.title.split(" ")[0]} {v.title.split(" ")[1] || ""}
                  </button>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* Value-add blocks */}
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {blocks.map(({ Comp, data }, i) => (
            <React.Fragment key={data.id}>
              <Comp data={data} index={i + 1} />
              {i < blocks.length - 1 && <div className="h-px bg-white/5" />}
            </React.Fragment>
          ))}
        </div>

        <ImpactSection />
        <ComparisonSection />

        {/* CTA band */}
        <section className="relative overflow-hidden py-24">
          <div className="bg-radial-glow absolute inset-0" />
          <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
            <Reveal>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-400/25 bg-cyan-400/10">
                <Hospital className="h-7 w-7 text-cyan-300" />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-display mt-6 text-3xl font-bold text-white sm:text-4xl">
                Ready to answer every patient call?
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <p className="mx-auto mt-4 max-w-xl text-slate-400">
                Get a live walkthrough of Arkision for your hospital — plus a free test account to try the agent yourself.
              </p>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link to="/#contact">
                  <Button size="lg" className="group rounded-full bg-cyan-400 px-8 font-semibold text-slate-950 transition-colors hover:bg-cyan-300">
                    Book a Demo <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <a href={`tel:${contactInfo.gccPartner.phone.replace(/\s/g, "")}`}>
                  <Button size="lg" variant="outline" className="rounded-full border-white/15 bg-transparent px-8 font-semibold text-white hover:border-cyan-400/50 hover:bg-cyan-400/5 hover:text-cyan-200">
                    Call {contactInfo.gccPartner.phone}
                  </Button>
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default IndustriesPage;
